import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { ChevronDown, ChevronUp, Shield, Lock, FileText, AlertCircle, Eye, Users, Mail } from 'lucide-react';
export default function Privacy() {
  const [openSections, setOpenSections] = useState({
    introduction: true,
    dataCollection: true,
    dataSharing: true,
    cookies: true,
    security: true,
    international: true,
    rights: true,
    complaints: true,
    changes: true,
    contact: true,
    schedule: true
  });
  const toggleSection = section => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  return <main className="min-h-screen bg-white" data-id="element-565">
      <Helmet data-id="element-566">
        <title data-id="element-567">Privacy and Cookie Policy | Medicolegal Psychiatry</title>
        <meta name="description" content="Privacy and Cookie Policy for Medicolegal Psychiatry, explaining how we collect, use, process, and protect your personal information." data-id="element-568" />
        <meta name="keywords" content="Privacy Policy, Cookie Policy, GDPR, Data Protection, Medicolegal Psychiatry" data-id="element-569" />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-primary text-white py-10" data-id="element-570">
        <div className="container mx-auto px-4" data-id="element-571">
          <div className="flex items-center justify-center mb-4" data-id="element-572">
            <Shield className="h-12 w-12 mr-4" data-id="element-573" />
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold" data-id="element-574">
              Privacy and Cookie Policy
            </h1>
          </div>
          <p className="text-lg max-w-3xl mx-auto text-center" data-id="element-575">
            Last Updated: September 12, 2025
          </p>
        </div>
      </section>
      {/* Policy Content */}
      <section className="py-12" data-id="element-576">
        <div className="container mx-auto px-4" data-id="element-577">
          <div className="max-w-3xl mx-auto" data-id="element-578">
            <p className="text-slate-dark text-lg mb-8" data-id="element-579">
              This Privacy and Cookie Policy explains how Medicolegal Psychiatry
              (referred to as "we," "us," or "our"), operated by Dr. Imtiaz
              Syed, collects, uses, processes, and protects your personal
              information when you use our services, visit our website
              (medicolegal-psychiatry.co.uk), or otherwise interact with us.
            </p>
            <div className="space-y-4 mb-8" data-id="element-580">
              {/* Introduction Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-581">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.introduction ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('introduction')} data-id="element-582">
                  <div className="flex items-center" data-id="element-583">
                    <FileText className="h-5 w-5 mr-2" data-id="element-584" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-585">
                      1. Introduction
                    </h2>
                  </div>
                  {openSections.introduction ? <ChevronUp className="h-5 w-5" data-id="element-586" /> : <ChevronDown className="h-5 w-5" data-id="element-587" />}
                </button>
                {openSections.introduction && <div className="p-6 bg-white" data-id="element-588">
                    <p className="text-slate-dark mb-4" data-id="element-589">
                      Welcome to Medicolegal Psychiatry. We are committed to
                      upholding the highest standards of privacy and data
                      protection in all our operations. This policy outlines our
                      practices for handling personal data, ensuring compliance
                      with the UK General Data Protection Regulation (UK GDPR),
                      the Data Protection Act 2018 (DPA 2018), and other
                      relevant legislation.
                    </p>
                  </div>}
              </div>
              {/* Data Collection Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-590">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.dataCollection ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('dataCollection')} data-id="element-591">
                  <div className="flex items-center" data-id="element-592">
                    <Eye className="h-5 w-5 mr-2" data-id="element-593" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-594">
                      2. How We Collect and Use Your Information
                    </h2>
                  </div>
                  {openSections.dataCollection ? <ChevronUp className="h-5 w-5" data-id="element-595" /> : <ChevronDown className="h-5 w-5" data-id="element-596" />}
                </button>
                {openSections.dataCollection && <div className="p-6 bg-white" data-id="element-597">
                    <p className="text-slate-dark mb-4" data-id="element-598">
                      We collect and process personal data primarily to provide
                      our medico-legal psychiatric assessments and expert
                      witness services. This includes:
                    </p>
                    <h3 className="text-lg font-bold text-primary mb-2" data-id="element-599">
                      a) Personal Information We Collect:
                    </h3>
                    <ul className="list-disc pl-6 mb-4 text-slate-dark" data-id="element-600">
                      <li className="mb-2" data-id="element-601">
                        Contact Details: Your name, title, professional address,
                        email address, and phone number.
                      </li>
                      <li className="mb-2" data-id="element-602">
                        Client Information: If you are an instructing party, we
                        will process personal data relating to your client,
                        including their name, contact details, date of birth,
                        photographic identification, and special category data
                        such as medical records, mental health history, and case
                        documentation.
                      </li>
                      <li className="mb-2" data-id="element-603">
                        Account Information: For our clients, this may include
                        usernames, passwords, security questions, and
                        preferences.
                      </li>
                      <li className="mb-2" data-id="element-604">
                        Financial Information: Payment details, transaction
                        history, and billing information.
                      </li>
                      <li className="mb-2" data-id="element-605">
                        Communications: Records of your interactions with us via
                        email, phone, or post.
                      </li>
                      <li className="mb-2" data-id="element-606">
                        Website Usage Data: Information collected through
                        cookies and similar technologies when you visit our
                        website.
                      </li>
                      <li data-id="element-607">
                        Professional Details: Information about your
                        professional standing, such as SRA registration status
                        and professional memberships.
                      </li>
                    </ul>
                    <h3 className="text-lg font-bold text-primary mb-2" data-id="element-608">
                      b) How We Use Your Information:
                    </h3>
                    <ul className="list-disc pl-6 mb-4 text-slate-dark" data-id="element-609">
                      <li className="mb-2" data-id="element-610">
                        Service Provision: To conduct assessments, prepare
                        medico-legal reports, provide expert witness testimony,
                        and manage appointments.
                      </li>
                      <li className="mb-2" data-id="element-611">
                        Contractual Obligations: To manage our relationship with
                        you, process invoices, and receive payments.
                      </li>
                      <li className="mb-2" data-id="element-612">
                        Legal & Regulatory Compliance: To comply with legal
                        obligations, professional requirements, and respond to
                        lawful requests from courts, tribunals, or regulatory
                        bodies.
                      </li>
                      <li className="mb-2" data-id="element-613">
                        Communication: To respond to your enquiries, provide
                        updates on your case, and send important information.
                      </li>
                      <li className="mb-2" data-id="element-614">
                        Business Improvement: To analyse website usage, improve
                        our services, and maintain security.
                      </li>
                      <li data-id="element-615">
                        Risk Management: To ensure the safety of our staff
                        during visits.
                      </li>
                    </ul>
                    <h3 className="text-lg font-bold text-primary mb-2" data-id="element-616">
                      c) Legal Grounds for Processing:
                    </h3>
                    <p className="text-slate-dark mb-2" data-id="element-617">
                      We process your personal data based on the following
                      lawful bases:
                    </p>
                    <ul className="list-disc pl-6 text-slate-dark" data-id="element-618">
                      <li className="mb-2" data-id="element-619">
                        Performance of a Contract: Necessary for providing our
                        medico-legal services as per your instruction.
                      </li>
                      <li className="mb-2" data-id="element-620">
                        Compliance with a Legal Obligation: To meet our duties
                        as a healthcare professional and expert witness, and for
                        regulatory compliance.
                      </li>
                      <li className="mb-2" data-id="element-621">
                        Legitimate Interests: To manage our business operations,
                        improve services, ensure security, and for effective
                        communication.
                      </li>
                      <li className="mb-2" data-id="element-622">
                        Consent: For processing special category data where
                        explicit consent is obtained, and for specific marketing
                        communications.
                      </li>
                      <li data-id="element-623">
                        Vital Interests: In rare cases, to protect the vital
                        interests of an individual.
                      </li>
                    </ul>
                  </div>}
              </div>
              {/* Data Sharing Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-624">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.dataSharing ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('dataSharing')} data-id="element-625">
                  <div className="flex items-center" data-id="element-626">
                    <Users className="h-5 w-5 mr-2" data-id="element-627" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-628">
                      3. Data Sharing
                    </h2>
                  </div>
                  {openSections.dataSharing ? <ChevronUp className="h-5 w-5" data-id="element-629" /> : <ChevronDown className="h-5 w-5" data-id="element-630" />}
                </button>
                {openSections.dataSharing && <div className="p-6 bg-white" data-id="element-631">
                    <p className="text-slate-dark mb-4" data-id="element-632">
                      We may share your personal information in the following
                      circumstances:
                    </p>
                    <ul className="list-disc pl-6 mb-4 text-slate-dark" data-id="element-633">
                      <li className="mb-2" data-id="element-634">
                        With You: As the Data Controller, we will share
                        information with you as required by our contract and
                        your instructions.
                      </li>
                      <li className="mb-2" data-id="element-635">
                        With Your Clients: If you are the instructing party, we
                        will share relevant information with your client as
                        necessary for the assessment and reporting process.
                      </li>
                      <li className="mb-2" data-id="element-636">
                        With Other Experts: If required for a joint report or to
                        obtain further information relevant to the case.
                      </li>
                      <li className="mb-2" data-id="element-637">
                        With Courts, Tribunals, and Regulatory Bodies: As
                        legally mandated.
                      </li>
                      <li className="mb-2" data-id="element-638">
                        With Third-Party Service Providers: For IT support,
                        secure cloud storage, and payment processing, all bound
                        by strict confidentiality agreements.
                      </li>
                      <li className="mb-2" data-id="element-639">
                        For Safety: If necessary, to ensure the safety of our
                        staff during site visits, we may arrange for an
                        accompanying person.
                      </li>
                      <li data-id="element-640">
                        With Legal or Regulatory Authorities: If required by law
                        or in response to valid legal process.
                      </li>
                    </ul>
                    <p className="text-slate-dark" data-id="element-641">
                      We do not sell your personal information.
                    </p>
                  </div>}
              </div>
              {/* Cookies Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-642">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.cookies ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('cookies')} data-id="element-643">
                  <div className="flex items-center" data-id="element-644">
                    <AlertCircle className="h-5 w-5 mr-2" data-id="element-645" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-646">
                      4. Cookies and Website Usage
                    </h2>
                  </div>
                  {openSections.cookies ? <ChevronUp className="h-5 w-5" data-id="element-647" /> : <ChevronDown className="h-5 w-5" data-id="element-648" />}
                </button>
                {openSections.cookies && <div className="p-6 bg-white" data-id="element-649">
                    <p className="text-slate-dark mb-4" data-id="element-650">
                      Our website, medicolegal-psychiatry.co.uk, uses cookies to
                      enhance your experience and improve our services.
                    </p>
                    <p className="text-slate-dark mb-4" data-id="element-651">
                      What are Cookies? Cookies are small text files placed on
                      your device when you visit a website. They help us
                      understand how visitors interact with our site, which
                      pages are popular, and how we can improve.
                    </p>
                    <p className="text-slate-dark mb-2" data-id="element-652">
                      Types of Cookies Used:
                    </p>
                    <ul className="list-disc pl-6 mb-4 text-slate-dark" data-id="element-653">
                      <li className="mb-2" data-id="element-654">
                        Strictly Necessary Cookies: Essential for the website to
                        function correctly.
                      </li>
                      <li className="mb-2" data-id="element-655">
                        Performance Cookies: Collect anonymous data on how
                        visitors use our site.
                      </li>
                      <li data-id="element-656">
                        Functionality Cookies: Remember choices you make to
                        provide a more personalised experience.
                      </li>
                    </ul>
                    <p className="text-slate-dark mb-4" data-id="element-657">
                      Managing Cookies: You can manage your cookie preferences
                      through your browser settings. Most browsers allow you to
                      block or delete cookies. However, disabling cookies may
                      affect some website functionalities.
                    </p>
                    <p className="text-slate-dark" data-id="element-658">
                      Analytics: We may use tools like Google Analytics to
                      understand website traffic and user behaviour.
                    </p>
                  </div>}
              </div>
              {/* Security Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-659">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.security ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('security')} data-id="element-660">
                  <div className="flex items-center" data-id="element-661">
                    <Lock className="h-5 w-5 mr-2" data-id="element-662" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-663">
                      5. Data Security and Retention
                    </h2>
                  </div>
                  {openSections.security ? <ChevronUp className="h-5 w-5" data-id="element-664" /> : <ChevronDown className="h-5 w-5" data-id="element-665" />}
                </button>
                {openSections.security && <div className="p-6 bg-white" data-id="element-666">
                    <p className="text-slate-dark mb-4" data-id="element-667">
                      We implement robust technical and organisational measures
                      to protect your personal data against unauthorised access,
                      loss, alteration, or disclosure. This includes secure data
                      storage, access controls, and regular security
                      assessments.
                    </p>
                    <p className="text-slate-dark" data-id="element-668">
                      We retain personal data only for as long as necessary to
                      fulfil the purposes outlined in this policy, comply with
                      legal and regulatory obligations, resolve disputes, and
                      enforce our agreements. Retention periods are based on
                      legal requirements (e.g., medical record retention) and
                      operational needs.
                    </p>
                  </div>}
              </div>
              {/* Additional sections follow the same pattern */}
              {/* International Data Transfers */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-669">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.international ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('international')} data-id="element-670">
                  <div className="flex items-center" data-id="element-671">
                    <FileText className="h-5 w-5 mr-2" data-id="element-672" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-673">
                      6. International Data Transfers
                    </h2>
                  </div>
                  {openSections.international ? <ChevronUp className="h-5 w-5" data-id="element-674" /> : <ChevronDown className="h-5 w-5" data-id="element-675" />}
                </button>
                {openSections.international && <div className="p-6 bg-white" data-id="element-676">
                    <p className="text-slate-dark" data-id="element-677">
                      Your personal information will primarily be processed
                      within the UK. If data is transferred outside the UK or
                      European Economic Area (EEA), we will ensure that
                      appropriate safeguards are in place, such as Standard
                      Contractual Clauses, to protect your data in accordance
                      with applicable laws.
                    </p>
                  </div>}
              </div>
              {/* Your Rights */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-678">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.rights ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('rights')} data-id="element-679">
                  <div className="flex items-center" data-id="element-680">
                    <Shield className="h-5 w-5 mr-2" data-id="element-681" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-682">
                      7. Your Rights
                    </h2>
                  </div>
                  {openSections.rights ? <ChevronUp className="h-5 w-5" data-id="element-683" /> : <ChevronDown className="h-5 w-5" data-id="element-684" />}
                </button>
                {openSections.rights && <div className="p-6 bg-white" data-id="element-685">
                    <p className="text-slate-dark mb-4" data-id="element-686">
                      As data subjects, you have several rights regarding your
                      personal data:
                    </p>
                    <ul className="list-disc pl-6 mb-4 text-slate-dark" data-id="element-687">
                      <li className="mb-2" data-id="element-688">
                        Right to Access: Request a copy of the personal data we
                        hold about you.
                      </li>
                      <li className="mb-2" data-id="element-689">
                        Right to Rectification: Request correction of inaccurate
                        or incomplete personal data.
                      </li>
                      <li className="mb-2" data-id="element-690">
                        Right to Erasure (Right to be Forgotten): Request
                        deletion of your personal data under certain conditions.
                      </li>
                      <li className="mb-2" data-id="element-691">
                        Right to Restriction of Processing: Request that we
                        limit the way we use your personal data.
                      </li>
                      <li className="mb-2" data-id="element-692">
                        Right to Data Portability: Request a transfer of your
                        personal data to you or another controller in a
                        structured, commonly used, and machine-readable format.
                      </li>
                      <li className="mb-2" data-id="element-693">
                        Right to Object: Object to processing based on
                        legitimate interests or for direct marketing purposes.
                      </li>
                      <li className="mb-2" data-id="element-694">
                        Rights Related to Automated Decision-Making and
                        Profiling: You have the right not to be subject to a
                        decision based solely on automated processing.
                      </li>
                      <li data-id="element-695">
                        Right to Withdraw Consent: If we rely on your consent
                        for processing, you have the right to withdraw it at any
                        time.
                      </li>
                    </ul>
                    <p className="text-slate-dark" data-id="element-696">
                      Exercising Your Rights: To exercise any of your rights,
                      please contact us in writing using the details provided
                      below. We may ask for proof of identity to verify your
                      request. We aim to respond to all legitimate requests
                      within one month. If your request is complex or numerous,
                      we may extend this period by two months.
                    </p>
                  </div>}
              </div>
              {/* Complaints */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-697">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.complaints ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('complaints')} data-id="element-698">
                  <div className="flex items-center" data-id="element-699">
                    <AlertCircle className="h-5 w-5 mr-2" data-id="element-700" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-701">
                      8. Complaints
                    </h2>
                  </div>
                  {openSections.complaints ? <ChevronUp className="h-5 w-5" data-id="element-702" /> : <ChevronDown className="h-5 w-5" data-id="element-703" />}
                </button>
                {openSections.complaints && <div className="p-6 bg-white" data-id="element-704">
                    <p className="text-slate-dark mb-4" data-id="element-705">
                      If you are unhappy with how we have handled your personal
                      data or any aspect of our privacy practices, you have the
                      right to lodge a complaint with the Information
                      Commissioner's Office (ICO), the UK's supervisory
                      authority for data protection issues.
                    </p>
                    <p className="text-slate-dark mb-2" data-id="element-706">
                      ICO Website: www.ico.org.uk
                    </p>
                    <p className="text-slate-dark mb-4" data-id="element-707">
                      ICO Helpline: 0303 123 1113
                    </p>
                    <p className="text-slate-dark" data-id="element-708">
                      We would appreciate the opportunity to address your
                      concerns directly before you approach the ICO. Please
                      contact us first.
                    </p>
                  </div>}
              </div>
              {/* Changes to Policy */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-709">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.changes ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('changes')} data-id="element-710">
                  <div className="flex items-center" data-id="element-711">
                    <FileText className="h-5 w-5 mr-2" data-id="element-712" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-713">
                      9. Changes to This Privacy and Cookie Policy
                    </h2>
                  </div>
                  {openSections.changes ? <ChevronUp className="h-5 w-5" data-id="element-714" /> : <ChevronDown className="h-5 w-5" data-id="element-715" />}
                </button>
                {openSections.changes && <div className="p-6 bg-white" data-id="element-716">
                    <p className="text-slate-dark" data-id="element-717">
                      We may update this Privacy and Cookie Policy from time to
                      time to reflect changes in our services, practices, or
                      legal requirements. Any changes will be posted on our
                      website with an updated "Last Updated" date. We encourage
                      you to review this policy regularly.
                    </p>
                  </div>}
              </div>
              {/* Contact Us */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-718">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.contact ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('contact')} data-id="element-719">
                  <div className="flex items-center" data-id="element-720">
                    <Mail className="h-5 w-5 mr-2" data-id="element-721" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-722">
                      10. Contact Us
                    </h2>
                  </div>
                  {openSections.contact ? <ChevronUp className="h-5 w-5" data-id="element-723" /> : <ChevronDown className="h-5 w-5" data-id="element-724" />}
                </button>
                {openSections.contact && <div className="p-6 bg-white" data-id="element-725">
                    <p className="text-slate-dark mb-4" data-id="element-726">
                      For any questions about this Privacy and Cookie Policy,
                      our data protection practices, or to exercise your rights,
                      please contact us:
                    </p>
                    <p className="text-slate-dark" data-id="element-727">
                      Email: enquiries@medicolegal-psychiatry.co.uk
                    </p>
                  </div>}
              </div>
              {/* Schedule */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-728">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.schedule ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('schedule')} data-id="element-729">
                  <div className="flex items-center" data-id="element-730">
                    <FileText className="h-5 w-5 mr-2" data-id="element-731" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-732">
                      Schedule 1: Processing, Personal Data, and Data Subjects
                    </h2>
                  </div>
                  {openSections.schedule ? <ChevronUp className="h-5 w-5" data-id="element-733" /> : <ChevronDown className="h-5 w-5" data-id="element-734" />}
                </button>
                {openSections.schedule && <div className="p-6 bg-white" data-id="element-735">
                    <p className="text-slate-dark mb-4" data-id="element-736">
                      This schedule provides further detail on the data
                      processing activities undertaken by Medicolegal
                      Psychiatry.
                    </p>
                    <h3 className="text-lg font-bold text-primary mb-2" data-id="element-737">
                      1. Processing by Medicolegal Psychiatry (as Data
                      Processor)
                    </h3>
                    <p className="text-slate-dark mb-4" data-id="element-738">
                      Purpose of Processing: To provide medico-legal services,
                      including assessments, expert opinion reports, and court
                      testimony, as instructed by you (the Data Controller).
                      This involves processing client personal data provided by
                      you.
                    </p>
                    <p className="text-slate-dark mb-4" data-id="element-739">
                      Scope: Processing client personal data necessary to
                      conduct assessments, arrange visits, communicate with
                      clients, and produce expert reports.
                    </p>
                    <p className="text-slate-dark mb-4" data-id="element-740">
                      Duration: For the duration of the contract and as long as
                      data retention is required by law or professional
                      guidelines.
                    </p>
                    <h3 className="text-lg font-bold text-primary mb-2" data-id="element-741">
                      2. Types of Personal Data Processed (relating to Clients)
                    </h3>
                    <ul className="list-disc pl-6 mb-4 text-slate-dark" data-id="element-742">
                      <li className="mb-2" data-id="element-743">
                        Contact Details: Name, address, phone number, email.
                      </li>
                      <li className="mb-2" data-id="element-744">
                        Special Categories of Personal Data: Medical records,
                        mental health assessments, information about conditions,
                        reports on lifestyle, family history, legal case
                        details, and relevant historical data.
                      </li>
                      <li className="mb-2" data-id="element-745">
                        Identification Data: Photographic identification, where
                        provided.
                      </li>
                      <li data-id="element-746">
                        Assigned Reference Numbers: Unique identifiers assigned
                        to clients.
                      </li>
                    </ul>
                    <h3 className="text-lg font-bold text-primary mb-2" data-id="element-747">
                      3. Categories of Data Subjects
                    </h3>
                    <ul className="list-disc pl-6 text-slate-dark" data-id="element-748">
                      <li className="mb-2" data-id="element-749">
                        The Subject: The individual undergoing assessment.
                      </li>
                      <li data-id="element-750">
                        The Instructing Party's Representatives: Case handlers,
                        solicitors, or other personnel from the instructing firm
                        who interact with us.
                      </li>
                    </ul>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>;
}